import React, {ReactNode, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {ReactSortable} from 'react-sortablejs';
import {ColumnInstance} from 'react-table';

import {ReactComponent as GrabIcon} from '../../../../assets/svg/draggable.svg';
import {ReactComponent as EyeIcon} from '../../../../assets/svg/eye.svg';
import {I18N_NS} from '../../../../core/constants/names';
import {showRestError} from '../../../../core/errors';
import {colors} from '../../../../theme';
import {ColumnExtendedProps, PinnedColumn} from '../../../../types/grid2.0';
import {Button, ButtonVariant} from '../../Button';
import {Icon} from '../../Icon';
import {IconButton} from '../../IconButton';
import {Modal} from '../../Modal';

import {HeaderWrapper} from '../HeaderWrapper';
import {UserConfig} from '../Table';

import {
    ActionsContainer,
    DraggableIcon,
    GroupedSettingHeader,
    GroupedSettingItem,
    SettingControls,
    SettingItem,
    SettingMain,
    SettingNumber,
    SettingsContainer,
} from './styled';

// Interface to support nested columns
// with parent column metadata and Header (for group's labels)
interface Column extends ColumnExtendedProps {
    parent?: any;
    Header?: any;
    id: string;
}

interface Group {
    id: string;
    name: string;
    columns: InnerColumn[];
}

interface Props {
    visible: boolean;
    onClose: Function;
    columns: Array<Column & ColumnInstance<object>>;
    defaultColumns: Array<string>;
    hiddenColumns?: Array<string>;
    pinnedColumns: Array<PinnedColumn>;
    setColumnOrder: (columns: string[]) => void;
    setHiddenColumns: (columns: string[]) => void;
    userConfig?: UserConfig;
}

// Internal column type for component state
interface InnerColumn extends ColumnExtendedProps {
    id: string;
    header: ReactNode;
    chosen?: boolean;
    active?: boolean;
    parentId?: string;
    parentName?: string;
}

export const Settings = (props: Props) => {
    const {t} = useTranslation(I18N_NS);
    const {
        visible,
        onClose,
        columns,
        defaultColumns,
        pinnedColumns,
        setColumnOrder,
        hiddenColumns = [],
        setHiddenColumns,
        userConfig,
    } = props;

    const [hiddenColumnsBuffer, setHiddenColumnsBuffer] = useState<string[]>([]);
    const [columnsBuffer, setColumnsBuffer] = useState<InnerColumn[]>([]);

    useEffect(() => {
        setHiddenColumnsBuffer(hiddenColumns);
        setColumnsBuffer(
            columns
                .filter((column) => !column.ignoreConfig)
                .map((item) => ({
                    id: item.id,
                    header: (item.Header.props as React.ComponentProps<typeof HeaderWrapper>)
                        ?.header,
                    active: hiddenColumns?.indexOf(item.id) === -1,
                    parentId: item.parent?.id,
                    ignoreConfig: item.ignoreConfig,
                    parentName: (item.parent?.Header.props as React.ComponentProps<
                        typeof HeaderWrapper
                    >)?.header as string,
                })),
        );
    }, [visible, columns, hiddenColumns]);

    const groups = useMemo(() => {
        const groupsList: Group[] = [];

        for (let i = 0; i < columnsBuffer.length; i++) {
            if (columnsBuffer[i].parentId === undefined) continue;
            const groupIndex = groupsList.findIndex(({id}) => id === columnsBuffer[i].parentId);
            if (groupIndex === -1) {
                groupsList.push({
                    id: columnsBuffer[i].parentId!,
                    name: columnsBuffer[i].parentName!,
                    columns: [columnsBuffer[i]],
                });
            } else {
                groupsList[groupIndex].columns.push(columnsBuffer[i]);
            }
        }

        return groupsList;
    }, [columnsBuffer]);

    const setOrder = (columns: InnerColumn[]) => {
        let equals = true;
        for (let i = 0; i < columns.length; i++) {
            if (columns[i].id !== columnsBuffer[i].id) {
                equals = false;
            }
        }
        if (equals) return;
        setColumnsBuffer(columns);
    };

    const setHidden = (column: InnerColumn) => {
        const isActive = !hiddenColumnsBuffer.includes(column.id);

        if (isActive && hiddenColumnsBuffer.length === columnsBuffer.length - 1) {
            // At least one column should be active
            return;
        }
        if (isActive) {
            setHiddenColumnsBuffer([...hiddenColumnsBuffer, column.id]);
        } else {
            setHiddenColumnsBuffer(hiddenColumnsBuffer.filter((name) => name !== column.id));
        }
    };

    const applyConfig = () => {
        // Insert pinned columns to original positions
        const columnsOrder: string[] = columnsBuffer.map((item) => item.id);
        for (const item of pinnedColumns) {
            columnsOrder.splice(item.position, 0, item.id);
        }

        setColumnOrder(columnsOrder);
        setHiddenColumns(hiddenColumnsBuffer);
        if (userConfig) {
            userConfig
                .saveConfig({
                    name: userConfig.gridName,
                    columns: columnsBuffer.map((item) => item.id),
                    hiddenColumns: hiddenColumnsBuffer,
                })
                .catch((err) => showRestError(err));
        }
    };

    const onReset = () => {
        setColumnOrder(defaultColumns);
        setHiddenColumns([]);
        if (userConfig) {
            userConfig.saveConfig({
                name: userConfig.gridName,
                hiddenColumns: [],
                columns: defaultColumns,
            });
        }
        onClose();
    };

    return (
        <Modal
            title={t('components.table.SettingsModal')}
            modalWidth={1}
            show={visible}
            onClose={onClose}
        >
            <SettingsContainer>
                {groups.length === 0 ? (
                    <ReactSortable
                        list={columnsBuffer}
                        setList={(items) => setOrder(items)}
                        animation={150}
                        delay={2}
                    >
                        {columnsBuffer.map((item, index) => (
                            <SettingItem
                                active={!hiddenColumnsBuffer.includes(item.id)}
                                draggable={true}
                                key={item.id}
                            >
                                <SettingMain>
                                    <SettingNumber>{index + 1}</SettingNumber>
                                    <span>{item.header}</span>
                                </SettingMain>
                                <SettingControls>
                                    <IconButton
                                        icon={
                                            <Icon
                                                source={EyeIcon}
                                                width={20}
                                                fillColor={colors.brandPrimary}
                                            />
                                        }
                                        onClick={() => setHidden(item)}
                                    />
                                    <DraggableIcon
                                        source={GrabIcon}
                                        fillColor={'#C4C4C4'}
                                        width={16}
                                    />
                                </SettingControls>
                            </SettingItem>
                        ))}
                    </ReactSortable>
                ) : (
                    // Nested columns
                    <ReactSortable
                        list={groups}
                        setList={(groups: Group[]) => {
                            setOrder(
                                groups.reduce(
                                    (accumulator, current) => accumulator.concat(current.columns),
                                    [] as InnerColumn[],
                                ),
                            );
                        }}
                        animation={150}
                        delay={2}
                    >
                        {groups.map((group, groupIndex) => (
                            <GroupedSettingItem key={group.id}>
                                <GroupedSettingHeader>
                                    <SettingMain>
                                        <SettingNumber>{groupIndex + 1}</SettingNumber>
                                        {group.name}
                                    </SettingMain>
                                    <DraggableIcon
                                        source={GrabIcon}
                                        fillColor={'#C4C4C4'}
                                        width={16}
                                    />
                                </GroupedSettingHeader>
                                {group.columns.map((item) => (
                                    <SettingItem
                                        active={!hiddenColumnsBuffer.includes(item.id)}
                                        grouped={true}
                                        draggable={true}
                                        key={item.id}
                                    >
                                        <SettingMain>
                                            <span>{item.header}</span>
                                        </SettingMain>
                                        <SettingControls>
                                            <IconButton
                                                icon={
                                                    <Icon
                                                        source={EyeIcon}
                                                        width={20}
                                                        fillColor={colors.brandPrimary}
                                                    />
                                                }
                                                onClick={() => setHidden(item)}
                                            />
                                        </SettingControls>
                                    </SettingItem>
                                ))}
                            </GroupedSettingItem>
                        ))}
                    </ReactSortable>
                )}
            </SettingsContainer>
            <ActionsContainer>
                <Button onClick={onReset} variant={ButtonVariant.Secondary}>
                    {t('components.table.Reset')}
                </Button>
                <Button
                    onClick={() => {
                        applyConfig();
                        onClose();
                    }}
                    variant={ButtonVariant.Normal}
                >
                    {t('components.table.Save')}
                </Button>
            </ActionsContainer>
        </Modal>
    );
};
